import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Package, Search, Eye } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export const VisitServiceRecords = () => {
  const navigate = useNavigate();
  const [visitServices, setVisitServices] = useState([]);
  const [services, setServices] = useState([]);
  const [pets, setPets] = useState([]);
  const [owners, setOwners] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [vsResp, servResp, petsResp, ownersResp, visitsResp] = await Promise.all([
        api.get('/visit-services'),
        api.get('/services'),
        api.get('/pets'),
        api.get('/owners'),
        api.get('/visits'),
      ]);
      setVisitServices(vsResp.data);
      setServices(servResp.data);
      setPets(petsResp.data);
      setOwners(ownersResp.data);
      setVisits(visitsResp.data);
    } catch (error) {
      toast.error('Failed to load service records');
    } finally {
      setLoading(false);
    }
  };

  const getService = (serviceId) => services.find((s) => s.id === serviceId);
  const getVisit = (visitId) => visits.find((v) => v.id === visitId);
  const getPet = (petId) => pets.find((p) => p.id === petId);
  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || '—';

  const filtered = visitServices.filter((vs) => {
    const service = getService(vs.service_id);
    const visit = getVisit(vs.visit_id);
    const pet = visit ? getPet(visit.pet_id) : null;
    const q = search.toLowerCase();
    return (
      (service?.name || '').toLowerCase().includes(q) ||
      (service?.category || '').toLowerCase().includes(q) ||
      pet?.name?.toLowerCase().includes(q) ||
      getOwnerName(pet?.owner_id).toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading service records...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold text-sage-800 mb-2">Service Records</h1>
          <p className="text-gray-600">All services rendered across visits</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by service, pet, owner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-xl"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-lg p-12 text-center text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          {search ? 'No records match your search.' : 'No service records found.'}
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-100">
            {filtered.map((vs) => {
              const service = getService(vs.service_id);
              const visit = getVisit(vs.visit_id);
              const pet = visit ? getPet(visit.pet_id) : null;
              return (
                <div key={vs.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sage-800">{pet?.name || '—'}</p>
                      <p className="text-sm text-gray-500">{getOwnerName(pet?.owner_id)}</p>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full flex-shrink-0" onClick={() => navigate(`/dashboard/visits/${vs.visit_id}`)}>
                      <Eye className="w-4 h-4 mr-1" />Visit
                    </Button>
                  </div>
                  <p className="text-sm font-medium text-gray-800">{service?.name || '—'}</p>
                  <p className="text-xs text-gray-500">{service?.category || '—'}</p>
                  <div className="flex gap-4 mt-1 text-xs text-gray-400">
                    <span>Qty: {vs.quantity}</span>
                    {vs.price != null && <span>Price: ${vs.price.toFixed(2)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop table view */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pet</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((vs) => {
                  const service = getService(vs.service_id);
                  const visit = getVisit(vs.visit_id);
                  const pet = visit ? getPet(visit.pet_id) : null;
                  return (
                    <TableRow key={vs.id} className="hover:bg-sage-50 transition-colors">
                      <TableCell className="font-semibold text-sage-800">{pet?.name || '—'}</TableCell>
                      <TableCell className="text-gray-600">{getOwnerName(pet?.owner_id)}</TableCell>
                      <TableCell className="font-medium">{service?.name || '—'}</TableCell>
                      <TableCell className="text-gray-600">{service?.category || '—'}</TableCell>
                      <TableCell className="text-gray-600">{vs.quantity}</TableCell>
                      <TableCell className="text-gray-600">
                        {vs.price != null ? `$${vs.price.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => navigate(`/dashboard/visits/${vs.visit_id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Visit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
